// Making a project: its name and its key. The key's rules are the server's,
// said beside it; the server checks them, and a refusal shows in its words.
// Once the project is made, the board shows it.
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { flushSync } from 'react-dom'
import { projectPath } from './routing'
import { useMounted } from '../components/layout'
import { useBoardState, useStore } from '../store/context'
import { needsServer } from '../components/needsServer'
import { useUnsavedChanges } from '../components/unsaved'
import { useWorkspaceDraft } from '../workspace/drafts'

export function NewProjectPage() {
  const store = useStore()
  const { offline } = useBoardState()
  const navigate = useNavigate()
  // Kept as the workspace keeps drafts, and asked about before they are thrown away.
  const [name, setName] = useWorkspaceDraft('project.new.name', '')
  const [key, setKey] = useWorkspaceDraft('project.new.key', '')
  const [busy, setBusy] = useState(false)
  useUnsavedChanges((name !== '' || key !== '') && !busy)
  const [problem, setProblem] = useState<string | null>(null)
  const mounted = useMounted()

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy || store.getState().offline) return
    setBusy(true)
    setProblem(null)
    const outcome = await store.createProject(key, name)
    // The person went elsewhere meanwhile: the picker shows the project.
    if (!mounted.current) return
    if (outcome.ok) {
      flushSync(() => { setName(''); setKey('') })
      navigate(projectPath(key, '/board'))
      return
    }
    setProblem(`Could not add the project: ${outcome.message}`)
    setBusy(false)
  }

  return (
      <section className="settings-section" aria-labelledby="new-project-title">
        <h2 id="new-project-title" className="settings-title">Add a project</h2>
        <form className="setup-form" onSubmit={(event) => void submit(event)}>
          <label className="field">
            <span>Project name</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required disabled={busy} />
          </label>
          <div className="field">
            <label htmlFor="new-project-key">Key</label>
            <input
              id="new-project-key"
              className="input"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              required
              disabled={busy}
              autoComplete="off"
              spellCheck={false}
              aria-describedby="new-project-key-hint"
            />
            <span id="new-project-key-hint" className="muted form-field-hint">
              What its issues' keys start with, as Poulpe in Poulpe-001: 1 to 12 letters and digits, starting with a letter.
            </span>
          </div>
          {problem && (
            <p className="form-problem" role="alert">
              {problem}
            </p>
          )}
          <div className="form-actions">
            <Link className="button" to="/board">
              Cancel
            </Link>
            <button type="submit" className="button button-primary" disabled={busy || offline} {...needsServer(offline)}>
              Add project
            </button>
          </div>
        </form>
      </section>
  )
}
